import React, { useState, useRef } from 'react';
import { useLocale } from 'locale';
import { noop } from 'lodash';
import { useIntersectionObserver } from 'hooks/useIntersectionObserver';
import { H3, SmallText, Text } from 'ds/components/typography';
import PoalimIcon from 'assets/svg/poalim.svg';
import { useScreenBreakpoint } from 'consts/breakpoints';
import {
  BANNER_LINK,
  INTEREST_RATE,
  MIN_YEARS,
  MAX_YEARS,
  YEARS_STEP,
  EQUITY_STEP,
  MIN_EQUITY_PERCENTAGE,
  DEFAULT_EQUITY_PERCENTAGE,
  DEFAULT_YEARS,
  MIN_EQUITY,
} from './constants';
import {
  MortgageCalculatorRoot,
  MortgageCalculatorHeader,
  MortgageCalculatorDisclaimer,
  MortgageCalculatorLink,
  MortgageCalculatorContent,
  MortgageCalculatorItem,
  MortgageCalculatorText,
  MortgageCalculatorValue,
  SeparatorLine,
  MortgageCalculatorSliderLegend,
  MortgageCalculatorItemsLine,
  MortgageCalculatorItemInterest,
} from './styled';
import { SpacingBlock } from 'components/spacing-block';
import { SliderRange } from 'ds/components/slider';

export interface MortgageCalculatorProps {
  price: number;
  onBannerClick?: () => void;
  bannerExpose?: () => void;
}

export const MortgageCalculator: React.FC<MortgageCalculatorProps> = ({
  price,
  onBannerClick = noop,
  bannerExpose = noop,
}) => {
  const quarterPrice = Math.ceil(price * (MIN_EQUITY_PERCENTAGE / 100) / EQUITY_STEP) * EQUITY_STEP;
  const minPrice = quarterPrice < MIN_EQUITY ? MIN_EQUITY : quarterPrice;
  const [ equity, setEquity ] = useState(Math.ceil(price * (DEFAULT_EQUITY_PERCENTAGE / 100) / EQUITY_STEP) * EQUITY_STEP);
  const [ years, setYears ] = useState(DEFAULT_YEARS);
  const [ hasExposed, setHasExposed ] = useState(false);
  const { t, formatMoney } = useLocale();
  const screen = useScreenBreakpoint();
  const isMobile = screen < 2;
  const isSmallScreen = screen === 1;
  const loan = price - equity;
  const bannerRef = useRef<HTMLDivElement>(null);
  const monthlyPayment = loan * (INTEREST_RATE / 100) / 12 / (1 - Math.pow(1 + INTEREST_RATE / 100 / 12, -years * 12));
  const MobileItemsWrapper = isSmallScreen ? MortgageCalculatorItemsLine : React.Fragment;

  useIntersectionObserver(([ entry ]) => {
    if (!hasExposed && entry.isIntersecting) {
      bannerExpose();
      setHasExposed(true);
    }
  }, [ bannerRef ], { threshold: 0.1 });

  return (
    <MortgageCalculatorRoot ref={bannerRef}>
      <MortgageCalculatorHeader>
        <H3 weight="bold">{t('poalim.mortgageCalculator.header')}</H3>
        <PoalimIcon width={isMobile ? 100 : 133} height={isMobile ? 17 : 22} />
      </MortgageCalculatorHeader>
      <MortgageCalculatorSliderLegend>
        <SmallText>
          {t('poalim.mortgageCalculator.equity.text')}
        </SmallText>
        <Text weight="bold">
          {formatMoney(equity)}
        </Text>
      </MortgageCalculatorSliderLegend>
      <SliderRange
        min={-price}
        max={-minPrice}
        step={EQUITY_STEP}
        minDelta={EQUITY_STEP}
        values={[ -equity, 0 ]}
        onChange={v => setEquity(Math.abs(v[0]))}
        mode="red"
      />
      <SpacingBlock mTop={2}/>
      <MortgageCalculatorSliderLegend>
        <SmallText>
          {t('poalim.mortgageCalculator.years.text')}
        </SmallText>
        <Text weight="bold">
          {years}
        </Text>
      </MortgageCalculatorSliderLegend>
      <SliderRange
        min={-MAX_YEARS}
        max={-MIN_YEARS}
        step={YEARS_STEP}
        minDelta={YEARS_STEP}
        values={[ -years, 0 ]}
        onChange={v => setYears(Math.abs(v[0]))}
        mode="red"
      />
      <MortgageCalculatorContent>
        <MobileItemsWrapper>
          <MortgageCalculatorItem>
            <MortgageCalculatorText>
              {t('poalim.mortgageCalculator.loan.text')}
            </MortgageCalculatorText>
            <MortgageCalculatorValue>
              {formatMoney(loan)}
            </MortgageCalculatorValue>
          </MortgageCalculatorItem>
          <MortgageCalculatorItemInterest>
            <MortgageCalculatorText>
              {t('poalim.mortgageCalculator.interestRate.text')}
            </MortgageCalculatorText>
            <MortgageCalculatorValue>
              {t('poalim.mortgageCalculator.interestRate', { value: INTEREST_RATE })}
            </MortgageCalculatorValue>
          </MortgageCalculatorItemInterest>
        </MobileItemsWrapper>
        <SeparatorLine />
        <MortgageCalculatorItem>
          <MortgageCalculatorText>
            {t('poalim.mortgageCalculator.monthlyPayment.text')}
          </MortgageCalculatorText>
          <MortgageCalculatorValue isPrice>
            {formatMoney(monthlyPayment)}
          </MortgageCalculatorValue>
        </MortgageCalculatorItem>
      </MortgageCalculatorContent>
      <MortgageCalculatorLink onClick={onBannerClick} target="_blank" rel="noreferrer nofollow" href={BANNER_LINK}>
        {t('poalim.mortgageCalculator.link')}
      </MortgageCalculatorLink>
      <MortgageCalculatorDisclaimer>
        <div dangerouslySetInnerHTML={{ __html: t('poalim.mortgageCalculator.disclaimer1') }} />
        <div>
          {t('poalim.mortgageCalculator.disclaimer2')}
        </div>
      </MortgageCalculatorDisclaimer>
    </MortgageCalculatorRoot>
  );
};
